import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { toast } from "sonner";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { ShoppingCart, ShoppingBag, Store as StoreIcon } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { useCart } from "@/hooks/useCart";
import { CartPanel } from "@/components/cart/CartPanel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = [
  { key: "all", label: "Tous", testId: "filter-all" },
  { key: "commerce", label: "Commerce", testId: "filter-commerce" },
  { key: "boutique", label: "Boutique", testId: "filter-boutique" },
  { key: "tailor", label: "Artisan", testId: "filter-tailor" },
];

export default function ProductsPage({ user, onLogout }) {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [cartOpen, setCartOpen] = useState(false);
  const cart = useCart();

  const addToCart = (product) => {
    if (!cart.add(product)) {
      toast.error("Votre panier contient déjà les produits d'une autre boutique. Validez ou videz-le d'abord.");
      return;
    }
    toast.success(`${product.name} ajouté au panier`);
  };

  const checkout = () => {
    setCartOpen(false);
    if (!user) {
      toast.error("Connectez-vous pour commander");
      sessionStorage.setItem("post_login_redirect", "/orders/new?cart=1");
      navigate("/auth");
      return;
    }
    navigate("/orders/new?cart=1");
  };

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const url = selectedCategory === "all"
          ? `${API}/products`
          : `${API}/products?category=${selectedCategory}`;
        const response = await axios.get(url);
        setProducts(response.data);
      } catch (error) {
        toast.error("Erreur lors du chargement des produits");
      } finally {
        setIsLoading(false);
      }
    };
    fetchProducts();
  }, [selectedCategory]);

  return (
    <div className="min-h-screen bg-[#0F172A] text-white" data-testid="products-page">
      {/* TOP NAV */}
      <nav className="bg-[#0F172A]/95 backdrop-blur-xl border-b border-white/10 px-4 md:px-8 lg:px-12 py-4 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-3">
            <img
              src="/woma-logo.png"
              alt="ARTEKNOS Logo"
              className="h-12 w-12 rounded-full object-cover ring-2 ring-[#D4AF37]/40 shadow-lg shadow-[#D4AF37]/20"
              data-testid="arteknos-logo"
            />
            <h1
              className="text-xl md:text-2xl font-bold text-white"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              Livraison Express
            </h1>
          </div>
          <div className="flex gap-3">
            {user ? (
              <>
                <Button
                  onClick={() => navigate(user.role === "vendor" ? "/vendor" : "/dashboard")}
                  variant="outline"
                  className="rounded-full bg-white/10 border-white/30 text-white hover:bg-white/20"
                  data-testid="products-dashboard-btn"
                >
                  Dashboard
                </Button>
                <Button
                  onClick={onLogout}
                  variant="outline"
                  className="rounded-full bg-white/10 border-white/30 text-white hover:bg-white/20"
                  data-testid="products-logout-btn"
                >
                  Déconnexion
                </Button>
              </>
            ) : (
              <>
                <Button
                  onClick={() => navigate("/")}
                  variant="outline"
                  className="rounded-full bg-white/10 border-white/30 text-white hover:bg-white/20"
                  data-testid="products-home-btn"
                >
                  Accueil
                </Button>
                <Button
                  onClick={() => navigate("/auth")}
                  className="bg-white text-[#003594] hover:bg-white/90 font-bold rounded-full"
                  data-testid="products-login-btn"
                >
                  Connexion
                </Button>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* HERO GRADIENT — NSIC blue → gold */}
      <div className="bg-gradient-to-br from-[#002F87] via-[#003594] to-[#D4AF37] px-4 md:px-8 lg:px-12 pt-10 pb-14">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-3">
            <StoreIcon className="w-7 h-7 text-white" />
            <h2
              className="text-3xl md:text-4xl font-black tracking-tight text-white"
              style={{ fontFamily: "Outfit, sans-serif" }}
              data-testid="products-page-title"
            >
              Boutiques et Produits
            </h2>
          </div>
          <p className="text-white/80 max-w-2xl text-sm md:text-base">
            Découvrez les catalogues certifiés ARTEKNOS × NSIC. Paiement sécurisé via escrow Wave —
            vos fonds ne sont libérés qu'après réception de votre commande.
          </p>
        </div>
      </div>

      {/* FILTERS + CONTENT */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 -mt-7 relative z-10 pb-16">
        {/* Filter chips card */}
        <Card className="bg-[#1A1A1A] border-white/10 border mb-8">
          <CardContent className="py-4 flex gap-2 flex-wrap">
            {CATEGORIES.map((c) => {
              const active = selectedCategory === c.key;
              return (
                <button
                  key={c.key}
                  onClick={() => setSelectedCategory(c.key)}
                  className={`px-5 py-2 rounded-full text-sm font-bold transition-colors ${
                    active
                      ? "bg-[#003594] text-white shadow-lg shadow-[#003594]/30"
                      : "bg-white/10 text-white/70 hover:bg-white/20"
                  }`}
                  data-testid={c.testId}
                >
                  {c.label}
                </button>
              );
            })}
          </CardContent>
        </Card>

        {/* Products grid / empty state */}
        {isLoading ? (
          <p className="text-center py-8 text-white/60">Chargement…</p>
        ) : products.length === 0 ? (
          <Card className="bg-[#1A1A1A] border-white/10 border">
            <CardContent className="py-16 text-center">
              <StoreIcon className="w-12 h-12 text-white/30 mx-auto mb-4" />
              <p className="text-white/70 text-base font-bold" data-testid="no-products-available">
                Aucun produit disponible dans cette catégorie
              </p>
              <p className="text-white/40 text-xs mt-2 uppercase tracking-widest">
                Les catalogues certifiés NSIC seront bientôt disponibles
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="flex gap-6 items-start">
          <div className="flex-1 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {products.map((product) => (
              <Card
                key={product.id}
                className="bg-[#1A1A1A] border-white/10 border hover:border-[#D4AF37]/40 hover:-translate-y-1 transition-all"
                data-testid={`product-${product.id}`}
              >
                <CardHeader className="p-0">
                  {product.image_url ? (
                    <img
                      src={product.image_url}
                      alt={product.name}
                      className="w-full h-48 object-cover rounded-t-2xl"
                    />
                  ) : (
                    <div className="w-full h-48 bg-white/5 flex items-center justify-center rounded-t-2xl">
                      <ShoppingCart className="w-12 h-12 text-white/30" />
                    </div>
                  )}
                </CardHeader>
                <CardContent className="p-4">
                  <h3 className="font-semibold text-lg mb-1 text-white">{product.name}</h3>
                  <p className="text-sm text-white/60 mb-2 line-clamp-2">{product.description}</p>
                  <p className="text-xl font-bold text-[#D4AF37] mb-2">
                    {new Intl.NumberFormat("fr-FR").format(product.price)} FCFA
                  </p>
                  <p className="text-sm text-white/50 mb-3">
                    {product.stock > 0 ? `Stock: ${product.stock}` : "Rupture de stock"}
                  </p>
                  <Button
                    className="w-full bg-[#003594] hover:bg-[#002F87] text-white rounded-full font-bold shadow-lg shadow-[#003594]/30"
                    onClick={() => addToCart(product)}
                    data-testid={`order-product-${product.id}`}
                  >
                    <ShoppingCart className="w-4 h-4 mr-2" /> Ajouter au panier
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="hidden lg:block w-80 flex-shrink-0 sticky top-24">
            <CartPanel cart={cart} onCheckout={checkout} />
          </div>
          </div>
        )}
      </div>

      {/* Mobile : bouton panier flottant + tiroir */}
      <button
        type="button"
        onClick={() => setCartOpen(true)}
        className="lg:hidden fixed bottom-6 right-6 z-50 bg-[#D4AF37] text-[#0F172A] rounded-full shadow-xl shadow-[#D4AF37]/30 px-5 py-4 flex items-center gap-2 font-black active:scale-95 transition-transform"
        data-testid="mobile-cart-btn"
      >
        <ShoppingBag className="w-5 h-5" />
        <span data-testid="mobile-cart-count">{cart.count}</span>
      </button>
      <Sheet open={cartOpen} onOpenChange={setCartOpen}>
        <SheetContent side="right" className="bg-[#0F172A] border-white/10 p-4 w-[90vw] sm:max-w-md overflow-y-auto">
          <SheetTitle className="sr-only">Mon panier</SheetTitle>
          <div className="mt-6">
            <CartPanel cart={cart} onCheckout={checkout} />
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
